import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight } from "lucide-react";
import { MaskLine } from "@/components/Reveal";

const HERO_BG =
    "https://images.unsplash.com/photo-1727461553668-45322401f863?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxvZmZzaG9yZSUyMG9pbCUyMHJpZyUyMG9jZWFufGVufDB8fHx8MTc4NTUyODA5Mnww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
    const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    return (
        <section
            ref={ref}
            id="top"
            data-testid="hero-section"
            className="relative min-h-screen overflow-hidden bg-abyss"
        >
            {/* Parallax background */}
            <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
                <img
                    src={HERO_BG}
                    alt="Offshore platform in the ocean"
                    className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-abyss/75" />
                <div className="absolute inset-0 bg-gradient-to-t from-abyss via-abyss/40 to-abyss/60" />
            </motion.div>

            {/* Foreground */}
            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 min-h-screen flex flex-col justify-center pt-24"
            >
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="flex items-center gap-3 mb-8"
                >
                    <span className="h-px w-12 bg-safety" />
                    <span className="font-mono-tech text-xs uppercase tracking-[0.3em] text-safety">
                        Offshore HVAC Specialists
                    </span>
                </motion.div>

                <h1 className="font-heading font-black tracking-tighter text-foreground text-[13vw] leading-[0.86] sm:text-7xl lg:text-[7.5rem] lg:leading-[0.85]">
                    <MaskLine delay={0.15}>CLIMATE</MaskLine>
                    <MaskLine delay={0.28}>
                        CONTROL <span className="text-safety">FOR</span>
                    </MaskLine>
                    <MaskLine delay={0.41}>HAZARD ZONES</MaskLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.8 }}
                    className="mt-8 max-w-xl text-base sm:text-lg text-steel leading-relaxed"
                >
                    Keva Service maintains air filtration, climate control and hazardous
                    gas pressurization systems — engineered exclusively for offshore rigs,
                    platforms and vessels.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.85, duration: 0.8 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <a
                        href="#contact"
                        data-testid="hero-cta"
                        className="group inline-flex items-center gap-2 bg-safety hover:bg-safety-hover text-white font-mono-tech text-xs uppercase tracking-[0.15em] px-7 py-4 transition-colors"
                    >
                        Request a Site Assessment
                        <ArrowDownRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:translate-y-1" />
                    </a>
                    <a
                        href="#services"
                        data-testid="hero-secondary"
                        className="inline-flex items-center gap-2 border border-border hover:border-safety text-foreground font-mono-tech text-xs uppercase tracking-[0.15em] px-7 py-4 transition-colors"
                    >
                        View Capabilities
                    </a>
                </motion.div>
            </motion.div>

            {/* Bottom-right technical metadata */}
            <motion.div
                style={{ opacity: fade }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="absolute bottom-8 right-6 lg:right-10 z-10 hidden sm:block text-right"
            >
                <div className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-steel space-y-1">
                    <p>LAT 58.9°N / LON 1.3°E</p>
                    <p>ATEX ZONE 1 · IECEx</p>
                    <p className="text-safety">SYSTEMS NOMINAL</p>
                </div>
            </motion.div>
        </section>
    );
}
